import { colors as themeColors } from '../utils/theme';
import { useState, useRef, useEffect } from 'react';
import { Box, Grid, Paper, TextField, Typography, IconButton, CircularProgress, InputAdornment, Snackbar, MenuItem, Button, Divider, Badge} from '@mui/material';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { MdShoppingCartCheckout } from 'react-icons/md';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

const API_URL = 'http://192.168.1.248:3103/api';

export default function OrderDetails_v2({ loggedInUser, companyData }) {
    // — Refs for focus management —
    const searchRef = useRef(null);    // ref to the search input field
    const cardRefs = useRef({});        // refs to each product card

    // — Search/filter state —
    const [searchTerm, setSearchTerm] = useState('');       // current search term
    const [hasSearched, setHasSearched] = useState(false);  // whether Enter was pressed

    // — Results state —
    const [products, setProducts] = useState([]);           // fetched product list
    const [loading, setLoading] = useState(false);          // loading spinner flag

    // — Per‐item UI state —
    const [quantities, setQuantities] = useState({});       // quantity per product
    const [expandedCardId, setExpandedCardId] = useState(null); // id of expanded card
    const [imageMap, setImageMap] = useState({});           // product images map
    const [carouselIndex, setCarouselIndex] = useState({}); // current carousel index per product
    const [pauseCarousel, setPauseCarousel] = useState({}); // pause carousel on hover

    // — Size/Color selection state —
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});
    const [selectedSizes, setSelectedSizes] = useState({});
    const [selectedColors, setSelectedColors] = useState({});
    const [selectionErrors, setSelectionErrors] = useState({});

    // — Cart & feedback state —
    const [snackbarOpen, setSnackbarOpen] = useState(false); // show "Added to Cart"
    const [bounce, setBounce] = useState(false);             // cart icon bounce animation

    const navigate = useNavigate();
    const { addItem, cartItems } = useCart();

    /** After mount, focus the search input **/
    useEffect(() => {
        searchRef.current?.focus();
    }, []);

    /** Load color & size master data once **/
    useEffect(() => {
        const fetchMasters = async () => {
            const [colRes, szRes] = await Promise.all([
                axios.get(`${API_URL}/colormaster`),
                axios.get(`${API_URL}/sizemaster`)
            ]);
            const colors = {}, sizes = {};
            colRes.data.forEach(c => colors[c.colorId] = { name: c.itemColor, code: c.colorCode });
            szRes.data.forEach(s => sizes[s.sizeId] = { name: s.itemSize, code: s.sizeCode });
            setColorMap(colors);
            setSizeMap(sizes);
        };
        fetchMasters();
    }, []);

    const colorOptions = Object.entries(colorMap).map(([id, v]) => ({ id: +id, ...v }));
    const sizeOptions = Object.entries(sizeMap).map(([id, v]) => ({ id: +id, ...v }));

    /** Bounce cart icon when there's at least one item **/
    useEffect(() => {
        if (!Object.keys(cartItems).length) return setBounce(false);
        const iv = setInterval(() => setBounce(b => !b), 2000);
        return () => clearInterval(iv);
    }, [cartItems]);

    /** Reset search results if the input is cleared **/
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setHasSearched(false);
            setProducts([]);
        }
    }, [searchTerm]);

    /** Handle Enter key on search input => perform search **/
    const handleSearchKey = async e => {
        if (e.key !== 'Enter') return;
        if (!searchTerm.trim()) return;
        setHasSearched(true);
        setLoading(true);

        try {
            const res = await axios.get(`${API_URL}/product/search`, {
                params: { mode: 'search', itemCode: searchTerm }
            });
            const items = Array.isArray(res.data) ? res.data : [];
            setProducts(items);

            // initialize quantities and image placeholders
            const qtys = {}, imgm = {};
            items.forEach(p => { qtys[p.Id] = p.minOrderQty || 1; imgm[p.Id] = [] });
            setQuantities(qtys);
            setImageMap(imgm);

            // fetch images for each product
            await Promise.all(items.map(async p => {
                try {
                    const r = await axios.get(`${API_URL}/product/images/${p.Id}`, {
                        headers: {
                            'Cache-Control': 'no-cache', Pragma: 'no-cache', Expires: '0',
                            'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT'
                        }
                    });
                    imgm[p.Id] = Array.isArray(r.data) && r.data.length
                        ? r.data.sort((a, b) => a.sequence - b.sequence)
                            .map(i => `data:${i.mimeType};base64,${i.image}`)
                        : [`https://picsum.photos/seed/${p.Id}/400/300`];
                } catch {
                    imgm[p.Id] = [`https://picsum.photos/seed/${p.Id}/400/300`];
                }
            }));
            setImageMap(imgm);
        } finally {
            setLoading(false);
        }
    };

    /** Auto-rotate carousel every 6s unless paused via hover **/
    useEffect(() => {
        const iv = setInterval(() => {
            setCarouselIndex(prev => {
                const next = { ...prev };
                Object.keys(imageMap).forEach(pid => {
                    if (imageMap[pid]?.length > 1 && !pauseCarousel[pid]) {
                        next[pid] = ((prev[pid] || 0) + 1) % imageMap[pid].length;
                    }
                });
                return next;
            });
        }, 6000);
        return () => clearInterval(iv);
    }, [imageMap, pauseCarousel]);

    /** Toggle card expand/collapse only when pressing Enter on the card wrapper **/
    const handleCardKey = (e, id) => {
        if (e.key === 'Enter' && e.currentTarget === cardRefs.current[id]) {
            setExpandedCardId(prev => (prev === id ? null : id));
        }
    };

    /** Add item to cart, validate size/color selection, then refocus search **/
    const handleAddToCart = p => {
        const id = p.Id;
        const qty = quantities[id];
        const sizeId = selectedSizes[id];
        const colorId = selectedColors[id];

        if (!sizeId || !colorId) {
            setSelectionErrors(prev => ({ ...prev, [id]: { size: !sizeId, color: !colorId } }));
            return;
        }
        setSelectionErrors(prev => ({ ...prev, [id]: {} }));
        addItem({ ...p, sizeId, colorId }, qty);
        setSnackbarOpen(true);
        setExpandedCardId(null);
        // refocus search input after adding to cart
        searchRef.current?.focus();
    };

    /** Carousel navigation helpers **/
    const prevImg = pid => {
        setCarouselIndex(i => ({
            ...i,
            [pid]: ((i[pid] || 0) - 1 + imageMap[pid].length) % imageMap[pid].length
        }));
    };
    const nextImg = pid => {
        setCarouselIndex(i => ({
            ...i,
            [pid]: ((i[pid] || 0) + 1) % imageMap[pid].length
        }));
    };

    return (
        <Box p={2} maxWidth="1200px" mx="auto">
            {/* Search bar & cart icon — relative, centered, responsive width */}
            <Box
                mx="auto"
                my={2}
                width={{ xs: '92%', sm: '80%', md: '60%' }}
                bgcolor="background.paper"
                boxShadow={6}
                borderRadius={3}
                px={{ xs: 2, sm: 3 }}
                py={1.5}
                display="flex"
                alignItems="center"
                gap={2}
            >
                <TextField
                    fullWidth
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKey}
                    inputRef={searchRef}  // attach ref for focus
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        )
                    }}
                />
                <Badge badgeContent={Object.keys(cartItems).length} color="error" invisible={!Object.keys(cartItems).length}>
                    <IconButton
                        onClick={() => navigate('/main/cart-page')}
                        sx={{
                            bgcolor: 'grey.100', borderRadius: 2, p: 1.2,
                            animation: bounce ? 'bounce 0.5s ease' : 'none',
                            '@keyframes bounce': {
                                '0%,100%': { transform: 'translateY(0)' },
                                '30%': { transform: 'translateY(-6px)' }
                            }
                        }}
                    >
                        <MdShoppingCartCheckout size={26} />
                    </IconButton>
                </Badge>
            </Box>

            {/* Only show results AFTER user has pressed Enter on a non-empty term */}
            {(!hasSearched || !searchTerm.trim()) ? null
                : loading ? (
                    <Box mt={12} textAlign="center"><CircularProgress /></Box>
                ) : products.length === 0 ? (
                    <Typography mt={12} textAlign="center" color="text.secondary">No products found.</Typography>
                ) : (
                    <Grid container spacing={3} mt={1}>
                        {products.map(p => {
                            const isExpanded = expandedCardId === p.Id;
                            const minQty = p.minOrderQty || 1;
                            const qty = quantities[p.Id] || minQty;

                            return (
                                <Grid item xs={6} sm={6} md={3} key={p.Id}>
                                    <Paper
                                        ref={el => (cardRefs.current[p.Id] = el)}
                                        tabIndex={0}
                                        elevation={isExpanded ? 6 : 2}
                                        onKeyDown={e => handleCardKey(e, p.Id)}
                                        onClick={() => setExpandedCardId(prev => (prev === p.Id ? null : p.Id))}
                                        sx={{
                                            borderRadius: 3, cursor: 'pointer', outline: 'none',
                                            '&:focus': { boxShadow: 4, outline: '2px solid', outlineColor: 'primary.main' }
                                        }}
                                    >
                                        {/* Image carousel area */}
                                        <Box
                                            sx={{ height: 200, position: 'relative', overflow: 'hidden', bgcolor: themeColors.neutral.surfaceMuted }}
                                            onMouseEnter={() => setPauseCarousel(pa => ({ ...pa, [p.Id]: true }))}
                                            onMouseLeave={() => setPauseCarousel(pa => ({ ...pa, [p.Id]: false }))}
                                        >
                                            {(imageMap[p.Id] || []).map((img, i) => (
                                                <Box
                                                    key={i}
                                                    component="img"
                                                    src={img}
                                                    alt=""
                                                    sx={{
                                                        position: 'absolute', top: 0, left: 0,
                                                        width: '100%', height: '100%', objectFit: 'contain',
                                                        opacity: (carouselIndex[p.Id] || 0) === i ? 1 : 0,
                                                        transition: 'opacity .5s ease-in-out'
                                                    }}
                                                />
                                            ))}
                                            {imageMap[p.Id]?.length > 1 && (
                                                <>
                                                    <IconButton size="small" sx={{ position: 'absolute', top: '50%', left: 8, translate: '0 -50%', bgcolor: 'rgba(255,255,255,0.7)' }} onClick={() => prevImg(p.Id)} onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); prevImg(p.Id); } }}>
                                                        <ArrowBackIosNewIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" sx={{ position: 'absolute', top: '50%', right: 8, translate: '0 -50%', bgcolor: 'rgba(255,255,255,0.7)' }} onClick={() => nextImg(p.Id)} onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); nextImg(p.Id); } }}>
                                                        <ArrowForwardIosIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </Box>

                                        {/* Card content */}
                                        <Box p={2}>
                                            <Typography fontWeight={600} noWrap>{p.itemCode}</Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap>{p.description}</Typography>
                                            <Typography variant="h6" color="primary" mt={1}>₹{p.saleRate}</Typography>

                                            {isExpanded && (
                                                <>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Min Order Qty: {minQty}</Typography>
                                                        {/* Quantity controls */}
                                                        <Box display="flex" alignItems="center" mt={1}>
                                                            <IconButton onClick={() => setQuantities(q => ({
                                                                ...q,
                                                                [p.Id]: Math.max(minQty, q[p.Id] - 1)
                                                            }))}>
                                                                <RemoveIcon fontSize="small" />
                                                            </IconButton>

                                                            {/* Dynamic‐width quantity input */}
                                                            <Box position="relative" mx={1}>
                                                                {/* Hidden span to measure the text width */}
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        visibility: 'hidden',
                                                                        whiteSpace: 'pre',
                                                                        fontSize: '0.875rem',
                                                                        fontFamily: 'Roboto, sans-serif',
                                                                        px: 1,
                                                                    }}
                                                                    ref={el => {
                                                                        if (el) {
                                                                            const width = el.offsetWidth + 20; // add padding
                                                                            document.documentElement.style.setProperty(
                                                                                `--qty-input-width-${p.Id}`,
                                                                                `${Math.max(40, width)}px`
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    {qty}
                                                                </Box>

                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={qty}
                                                                    onClick={e => e.stopPropagation()}
                                                                    inputProps={{ min: minQty }}
                                                                    sx={{
                                                                        width: `var(--qty-input-width-${p.Id}, 40px)`,
                                                                        '& input': {
                                                                            textAlign: 'center',
                                                                            padding: '4px 8px'
                                                                        }
                                                                    }}
                                                                    onChange={e => {
                                                                        const v = parseInt(e.target.value, 10);
                                                                        if (!isNaN(v) && v >= minQty) {
                                                                            setQuantities(q => ({ ...q, [p.Id]: v }));
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>

                                                            <IconButton onClick={() => setQuantities(q => ({
                                                                ...q,
                                                                [p.Id]: q[p.Id] + 1
                                                            }))}>
                                                                <AddIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>

                                                        <Grid container spacing={2} mt={1}>
                                                            <Grid item xs={6}>
                                                                <TextField select label="Size" fullWidth value={selectedSizes[p.Id] || ''} error={selectionErrors[p.Id]?.size} onKeyDown={e => e.key === 'Enter' && e.stopPropagation()} onChange={e => { setSelectedSizes(s => ({ ...s, [p.Id]: +e.target.value })); setSelectionErrors(prev => ({ ...prev, [p.Id]: { ...prev[p.Id], size: false } })); }}>
                                                                    {sizeOptions.map(opt => <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>)}
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <TextField select label="Color" fullWidth value={selectedColors[p.Id] || ''} error={selectionErrors[p.Id]?.color} onKeyDown={e => e.key === 'Enter' && e.stopPropagation()} onChange={e => { setSelectedColors(c => ({ ...c, [p.Id]: +e.target.value })); setSelectionErrors(prev => ({ ...prev, [p.Id]: { ...prev[p.Id], color: false } })); }}>
                                                                    {colorOptions.map(opt => (
                                                                        <MenuItem key={opt.id} value={opt.id}>
                                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: opt.code, border: `1px solid ${themeColors.neutral.lineStrong}` }} />
                                                                                <Typography>{opt.name}</Typography>
                                                                            </Box>
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            </Grid>
                                                        </Grid>

                                                        <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={() => handleAddToCart(p)} onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); handleAddToCart(p); } }}>
                                                            Add to Cart
                                                        </Button>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )
            }

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message="Added to Cart"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}